// @givmo/board — sending an issued packet version to the board, and what came back.
//
// ══ A NOTICE FOR A MEMBER WHO HAS JOINED, AN INVITATION FOR ONE WHO HASN'T (CBG-SEC2) ════════════
//
// A member who has joined with their Givmo account gets a notice linking straight to the packet, with
// no token: their account is the way in. Every other active seat gets an INVITATION carrying this
// version, so joining opens the packet (contract §12.4). The response returns each invitation's
// plaintext token EXACTLY ONCE — plan §1.7's secretary path made literal: an officer hands a member
// their invitation as often as the module mails it. This panel holds those tokens in component state
// for as long as the officer stays on the page, and says plainly that they will not be shown again.
//
// Each invited seat's row shows and copies its LINK — the same address the mail carries, this version
// beside the token (CBG-FIX14, `director/director-access-link.ts`) — never the bare token.
//
// ══ "NO EMAIL" IS A STATE, NOT A FAILURE ════════════════════════════════════════════════════════
//
// Two structural skips are ordinary: no board-app base URL configured (there is nothing to put in a
// mail, and guessing a host sends directors somewhere that may not exist) and no configured mail
// template. In both the grants and the distribution rows are already durable, and the mails run AFTER
// the commit and never gate anything. So the row says which state it is and points at the link —
// rendering it red would send an officer chasing a bug while a director waits.
//
// ══ ENGAGEMENT IS MEASUREMENT, NOT SHAMING ══════════════════════════════════════════════════════
//
// Plan §1.7 sets the frame and the copy holds it: an open count and a date, no ranking, no "still
// hasn't opened it", and nothing inferred about whether a director is prepared. A seat with no receipt
// reads as an absence of a recording — which is the only true statement, since a director who read
// their papers on paper leaves none.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { extractBoardMembers, seatConnection } from '../roster/roster-decode';
import {
  deliveryLine,
  displayInstant,
  distributionCountLine,
  extractDistributions,
  extractEngagement,
  openReceiptLine,
  receiptsBySeat,
} from './packets-decode';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import { breakableLink } from '../../components/breakable-link';
import { useDirectorAccessLink } from '../../director/director-access-link';

export interface DistributionPanelProps {
  orgRef: string;
  packetId: string;
  version: number;
}

/** Plaintext tokens from THIS page's distribution call. The backend returns them once, and only once. */
type MintedTokens = Record<string, string>;

export function DistributionPanel({ orgRef, packetId, version }: DistributionPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const linkFor = useDirectorAccessLink();
  // Sending mints each director an access link — the backend's `board_member:grant_access`.
  const canSend = useBoardPermission('board_member:grant_access');
  // Who has opened what is the chair's engagement view, and its read is `board_member:grant_access`
  // too (`list_engagement`), not the Board read. Without it the read is refused, so it is not sent
  // and no opens line is drawn: "No opens recorded" would be false to someone who cannot see them.
  const canSeeOpens = useBoardPermission('board_member:grant_access');
  const [minted, setMinted] = useState<MintedTokens>({});
  const [error, setError] = useState<string | null>(null);
  const [copiedSeatId, setCopiedSeatId] = useState<string | null>(null);

  const distributionsOptions = operations.officerListBoardPacketDistributionsOptions({
    path: { org_ref: orgRef, packet_id: packetId },
  });
  const distributionsQuery = useQuery({
    ...distributionsOptions,
    select: extractDistributions,
  });
  const distributions = distributionsQuery.data ?? [];

  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: extractBoardMembers,
  });
  const seatsById = new Map<string, OrgBoardMemberOut>(
    (seatsQuery.data ?? []).map((seat) => [seat.id, seat]),
  );

  const engagementOptions = operations.officerListBoardEngagementOptions({
    path: { org_ref: orgRef },
    query: { artifact_kind: 'board_packet', artifact_ref: packetId },
  });
  const engagementQuery = useQuery({
    ...engagementOptions,
    enabled: canSeeOpens,
    select: extractEngagement,
  });
  const receipts = receiptsBySeat(engagementQuery.data ?? []);

  const distribute = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerDistributeBoardPacket({
        path: { org_ref: orgRef, packet_id: packetId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (result) => {
      setError(null);
      // Keep only string values: the wire types this map loosely, and a non-string here would render
      // as "[object Object]" in a field an officer is about to read out over the phone.
      const tokens: MintedTokens = {};
      for (const [seatId, token] of Object.entries(result.access_tokens ?? {})) {
        if (typeof token === 'string' && token.length > 0) tokens[seatId] = token;
      }
      setMinted(tokens);
      await queryClient.invalidateQueries({ queryKey: distributionsOptions.queryKey });
      const seats = result.seats_distributed ?? 0;
      toast.success(
        `Version ${version} went to ${seats} ${seats === 1 ? 'seat' : 'seats'}${
          (result.emails_sent ?? 0) > 0 ? `, ${result.emails_sent} by email` : ''
        }.`,
      );
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t send this out. Please try again.',
      );
    },
  });

  async function copyLink(seatId: string, link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedSeatId(seatId);
      toast.success('Link copied.');
    } catch {
      // The link is on screen and selectable, so a refused clipboard is a missing convenience.
      toast.error('We couldn’t copy it — you can select the link and copy it yourself.');
    }
  }

  const hasSent = distributions.length > 0;

  return (
    <Card testId="packet-distribution">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-center justify-between gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            Sending it to the board
          </h2>
          {/* Sending invites members who haven't joined (`board_member:grant_access`): without it, no
              button. */}
          {canSend ? (
            <Button
              appearance="saas"
              tone={hasSent ? 'secondary' : 'primary'}
              size="sm"
              loading={distribute.isPending}
              onClick={() => {
                setError(null);
                distribute.mutate();
              }}
              testId="packet-distribute"
            >
              {hasSent ? 'Send it again' : 'Send it to the board'}
            </Button>
          ) : null}
        </div>

        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('packet-distribution-who')}
        >
          Members who’ve joined get a notice that it’s ready. Everyone else gets an invitation,
          which opens it once they join.
        </p>

        {error !== null ? (
          <ErrorMessage testId="packet-distribution-error">{error}</ErrorMessage>
        ) : null}

        {Object.keys(minted).length > 0 ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            {...testIdProp('packet-distribution-tokens-note')}
          >
            The invitation links below are shown once. If you leave this page you can still send it
            again, which makes new ones.
          </p>
        ) : null}

        {distributionsQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-lg" testId="packet-distribution-loading" />
        ) : null}

        {distributionsQuery.isSuccess && !hasSent ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp('packet-distribution-empty')}
          >
            This version hasn’t gone out yet.
          </p>
        ) : null}

        {hasSent ? (
          <ul className="flex flex-col gap-8" data-testid="packet-distribution-list">
            {distributions.map((distribution) => {
              const seat = seatsById.get(distribution.board_member_id) ?? null;
              const delivery = deliveryLine(distribution, {
                joined: seat !== null && seatConnection(seat) === 'joined',
              });
              const repeats = distributionCountLine(distribution);
              const token = minted[distribution.board_member_id] ?? null;
              const link = token === null ? null : linkFor(token, packetId);
              return (
                <li
                  key={distribution.id}
                  className="flex flex-col gap-8 rounded-lg border border-border-subtle p-12"
                  data-testid={`packet-distribution-${distribution.board_member_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-10">
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {seat?.display_name ?? 'A seat on this board'}
                      </span>
                      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                        {displayInstant(
                          distribution.last_distributed_at ?? distribution.first_distributed_at,
                        )}
                        {repeats !== null ? ` · ${repeats}` : ''}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-8">
                      <Badge tone={delivery.tone}>{delivery.label}</Badge>
                      {canSeeOpens ? (
                        <span
                          className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                          data-testid={`packet-opens-${distribution.board_member_id}`}
                        >
                          {openReceiptLine(receipts.get(distribution.board_member_id))}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {delivery.detail !== null ? (
                    <span
                      className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                      data-testid={`packet-delivery-detail-${distribution.board_member_id}`}
                    >
                      {delivery.detail}
                    </span>
                  ) : null}

                  {link !== null ? (
                    <div className="flex flex-wrap items-center gap-10">
                      <code
                        className="min-w-0 flex-1 wrap-anywhere rounded-md border border-border-subtle bg-surface-sunken p-10 font-mono text-s12 text-text-strong"
                        data-testid={`packet-link-${distribution.board_member_id}`}
                      >
                        {breakableLink(link)}
                      </code>
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="sm"
                        onClick={() => void copyLink(distribution.board_member_id, link)}
                        testId={`packet-link-copy-${distribution.board_member_id}`}
                      >
                        {copiedSeatId === distribution.board_member_id ? 'Copied' : 'Copy link'}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {hasSent && canSeeOpens ? (
          <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            Opens are counted so you know the packet reached people. A director who reads their
            papers on paper leaves none recorded.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
